import { useState, useCallback } from 'react';
import { message } from 'antd';
import { useAtom } from 'jotai';
import {
  currentFormSchemaAtom,
  formMetadataAtom,
  uploadedSqliteFilesAtom,
  currentTemplateUuidAtom,
  formBuilderLoadingAtom,
} from '~/stores/FormBuilderStore';
import { FormField_V2, FieldType_V2, FormSection } from '../../../common/typescript/models/CollectFormV2';
import { CollectFormTemplateAPIs } from '../../../common/typescript/apis/CollectFormTemplateAPIs';
import { v4 as uuidv4 } from 'uuid';

export const useFormBuilder = () => {
  const [formSchema, setFormSchema] = useAtom(currentFormSchemaAtom);
  const [metadata, setMetadata] = useAtom(formMetadataAtom);
  const [uploadedFiles, setUploadedFiles] = useAtom(uploadedSqliteFilesAtom);
  const [templateUuid, setTemplateUuid] = useAtom(currentTemplateUuidAtom);
  const [loading, setLoading] = useAtom(formBuilderLoadingAtom);

  // Add field to form
  const addField = useCallback(
    (field: FormField_V2) => {
      setFormSchema((prev) => ({
        ...prev,
        fields: [...prev.fields, field],
      }));
    },
    [setFormSchema],
  );

  // Update existing field
  const updateField = useCallback(
    (fieldId: string, updatedField: FormField_V2) => {
      setFormSchema((prev) => ({
        ...prev,
        fields: prev.fields.map((f) => (f.id === fieldId ? updatedField : f)),
      }));
    },
    [setFormSchema],
  );

  // Delete field
  const deleteField = useCallback(
    (fieldId: string) => {
      setFormSchema((prev) => ({
        ...prev,
        fields: prev.fields.filter((f) => f.id !== fieldId),
      }));
    },
    [setFormSchema],
  );

  // Reorder fields
  const reorderFields = useCallback(
    (fromIndex: number, toIndex: number) => {
      setFormSchema((prev) => {
        const newFields = [...prev.fields];
        const [removed] = newFields.splice(fromIndex, 1);
        newFields.splice(toIndex, 0, removed);
        return {
          ...prev,
          fields: newFields,
        };
      });
    },
    [setFormSchema],
  );

  // Create new field from type
  const createFieldFromType = useCallback((type: FieldType_V2): FormField_V2 => {
    const id = `field_${Date.now()}`;
    return {
      id,
      label: `New ${type} Field`,
      type,
      required: false,
      placeholder: '',
    };
  }, []);

  // Save template
  const saveTemplate = useCallback(async () => {
    if (!metadata.name.trim()) {
      message.error('Please enter a form name');
      return false;
    }

    if (formSchema.fields.length === 0) {
      message.error('Please add at least one field');
      return false;
    }

    try {
      setLoading(true);

      const templateData = {
        name: metadata.name,
        description: metadata.description,
        content: formSchema,
        sqlite_files: uploadedFiles.length > 0 ? uploadedFiles.join(',') : undefined,
      };

      if (templateUuid) {
        // Update existing
        await CollectFormTemplateAPIs.remote_updateTemplate(templateUuid, templateData);
        message.success('Template updated successfully');
      } else {
        // Create new
        const response = await CollectFormTemplateAPIs.remote_createTemplate(templateData);
        setTemplateUuid(response.data.uuid);
        message.success('Template created successfully');
      }

      return true;
    } catch (error) {
      console.error('[useFormBuilder] Save failed:', error);
      message.error('Failed to save template');
      return false;
    } finally {
      setLoading(false);
    }
  }, [metadata, formSchema, uploadedFiles, templateUuid, setLoading, setTemplateUuid]);

  // Load template
  const loadTemplate = useCallback(
    async (uuid: string) => {
      try {
        setLoading(true);
        const template = await CollectFormTemplateAPIs.remote_getOneTemplate(uuid);

        setMetadata({
          name: template.name,
          description: template.description || '',
        });
        setFormSchema(template.content);
        setTemplateUuid(template.uuid);

        if (template.sqlite_files) {
          setUploadedFiles(template.sqlite_files.split(','));
        }

        message.success('Template loaded successfully');
      } catch (error) {
        console.error('[useFormBuilder] Load failed:', error);
        message.error('Failed to load template');
      } finally {
        setLoading(false);
      }
    },
    [setLoading, setMetadata, setFormSchema, setTemplateUuid, setUploadedFiles],
  );

  // Reset form
  const resetForm = useCallback(() => {
    setFormSchema({
      fields: [],
      metadata: {
        version: '2.0.0',
        createdWith: 'GeoCloudWeb',
      },
    });
    setMetadata({ name: '', description: '' });
    setUploadedFiles([]);
    setTemplateUuid(null);
  }, [setFormSchema, setMetadata, setUploadedFiles, setTemplateUuid]);

  // Section Management
  const addSection = useCallback(
    (section: any) => {
      setFormSchema((prev) => ({
        ...prev,
        sections: [...(prev.sections || []), section],
      }));
    },
    [setFormSchema],
  );

  const updateSection = useCallback(
    (sectionId: string, updatedSection: any) => {
      setFormSchema((prev) => ({
        ...prev,
        sections: (prev.sections || []).map((s) => (s.id === sectionId ? updatedSection : s)),
      }));
    },
    [setFormSchema],
  );

  const deleteSection = useCallback(
    (sectionId: string) => {
      setFormSchema((prev) => ({
        ...prev,
        // Remove section
        sections: (prev.sections || []).filter((s) => s.id !== sectionId),
        // Reset section property for fields in this section
        fields: prev.fields.map((f) => (f.section === sectionId ? { ...f, section: undefined } : f)),
      }));
    },
    [setFormSchema],
  );

  return {
    formSchema,
    metadata,
    uploadedFiles,
    templateUuid,
    loading,
    addField,
    updateField,
    deleteField,
    reorderFields,
    createFieldFromType,
    saveTemplate,
    loadTemplate,
    resetForm,
    setMetadata,
    setUploadedFiles,
    setFormSchema,
    setLoading,
    addSection,
    updateSection,
    deleteSection,
  };
};
